import { useTranslation } from 'react-i18next';

import { MFA_TOTP_CONTRACT } from '@repo/common';
import { DataTestIds } from '@repo/frontend-shared';

import './mfa-otp-input.scss';

const MFA = DataTestIds.COMMON.SECURITY.MFA;
const OTP_DIGITS = MFA_TOTP_CONTRACT.digits;
const OTP_SLOT_IDS = ['d1', 'd2', 'd3', 'd4', 'd5', 'd6'] as const;

const RECOVERY_CODE_PATTERN = /[0-9A-F]{4}-[0-9A-F]{4}-[0-9A-F]{4}-[0-9A-F]{4}/i;

interface MfaOtpInputProps {
  dataTestId?: string;
  disabled?: boolean;
  onChange: (value: string) => void;
  value: string;
}

export function MfaOtpInput({ dataTestId, disabled = false, onChange, value }: MfaOtpInputProps) {
  const { t } = useTranslation('common');
  const digits = value.replace(/\D/g, '').slice(0, OTP_DIGITS);
  const focusIndex = Math.min(digits.length, OTP_DIGITS - 1);

  return (
    <div className="MfaOtpInput">
      <input
        aria-label={t('security.mfa.codeLabel')}
        autoComplete="one-time-code"
        className="MfaOtpInput__field"
        data-testid={dataTestId ?? MFA.CODE_INPUT}
        disabled={disabled}
        inputMode="numeric"
        maxLength={64}
        onChange={event => {
          const raw = event.target.value;
          if (RECOVERY_CODE_PATTERN.test(raw)) {
            onChange(raw);
          } else {
            onChange(raw.replace(/\D/g, '').slice(0, OTP_DIGITS));
          }
        }}
        pattern="[0-9]*"
        value={digits}
      />
      <div aria-hidden className="MfaOtpInput__boxes">
        {OTP_SLOT_IDS.slice(0, 3).map((slotId, index) => (
          <span className={`MfaOtpInput__box${index === focusIndex ? ' MfaOtpInput__box--focus' : ''}`} key={slotId}>
            {digits[index] ?? ''}
          </span>
        ))}
        <span className="MfaOtpInput__dash" />
        {OTP_SLOT_IDS.slice(3).map((slotId, index) => (
          <span
            className={`MfaOtpInput__box${index + 3 === focusIndex ? ' MfaOtpInput__box--focus' : ''}`}
            key={slotId}
          >
            {digits[index + 3] ?? ''}
          </span>
        ))}
      </div>
    </div>
  );
}
